import {
    ChevronDown,
    ChevronUp,
    ExternalLink,
    History,
    LayoutDashboard,
    RefreshCw,
    Trash2,
    TrendingDown
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import accountSummaryService from "../services/accountSummaryService";
import { IbkrAccountSummary, IbkrAccountSummaryHistory } from "../types";
import AdminAccountDetailModal from "./AdminAccountDetailModal";
import ConfirmationModal from "./ConfirmationModal";

interface AdminAccountSummaryProps {
  appearance?: "default" | "alpha";
  userId: string;
  userName?: string;
  advisorMode?: boolean;
  fetchSummaryOverride?: (userId: string, maxAgeMs?: number) => Promise<IbkrAccountSummary>;
}

type FeedbackState = {
  type: "error" | "success";
  message: string;
} | null;

const AdminAccountSummary: React.FC<AdminAccountSummaryProps> = ({
  appearance = "default", userId, userName, advisorMode = false, fetchSummaryOverride }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const navigate = useNavigate();

  const [summary, setSummary] = useState<IbkrAccountSummary | null>(null);
  const [history, setHistory] = useState<IbkrAccountSummaryHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deletePositionsConfirmOpen, setDeletePositionsConfirmOpen] = useState(false);
  const [deletingPositions, setDeletingPositions] = useState(false);

  // Ref-based guard to discard stale responses when userId changes.
  // Protects all entry points: useEffect, refresh button, "Fetch from IBKR" button.
  const currentUserIdRef = useRef<string>(userId);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    currentUserIdRef.current = userId;
  }, [userId]);

  const clearFeedbackTimer = useCallback(() => {
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearFeedbackTimer();
    };
  }, [clearFeedbackTimer]);

  const fetchSummary = useCallback(async (forceRefresh = false) => {
    const fetchForUserId = userId;
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      clearFeedbackTimer();
      setFeedback(null);
      const maxAge = forceRefresh ? 0 : undefined;
      const data = fetchSummaryOverride
        ? await fetchSummaryOverride(fetchForUserId, maxAge)
        : await accountSummaryService.getAdminUserAccountSummary(fetchForUserId, maxAge);
      if (currentUserIdRef.current === fetchForUserId) {
        setSummary(data);
      }
    } catch (err: any) {
      if (currentUserIdRef.current === fetchForUserId) {
        // 404 is expected if no summary exists yet
        if (err.response?.status !== 404) {
          console.error("Failed to fetch account summary:", err);
          setFeedback({ type: "error", message: "Failed to load account summary" });
        } else {
          setSummary(null);
        }
      }
    } finally {
      if (currentUserIdRef.current === fetchForUserId) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [clearFeedbackTimer, userId, fetchSummaryOverride]);

  const fetchHistory = useCallback(async () => {
    const fetchForUserId = userId;
    try {
      setLoadingHistory(true);
      const data = await accountSummaryService.getAdminUserAccountSummaryHistory(fetchForUserId);
      if (currentUserIdRef.current === fetchForUserId) {
        setHistory(data.history);
      }
    } catch (err) {
      if (currentUserIdRef.current === fetchForUserId) {
        console.error("Failed to fetch history:", err);
      }
    } finally {
      if (currentUserIdRef.current === fetchForUserId) {
        setLoadingHistory(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (showHistory) {
      fetchHistory();
    }
  }, [fetchHistory, showHistory]);

  const handleDelete = async () => {
    const fetchForUserId = userId;
    try {
      setDeleting(true);
      await accountSummaryService.deleteAdminUserAccountSummary(fetchForUserId);
      if (currentUserIdRef.current === fetchForUserId) {
        setSummary(null);
        setHistory([]);
        setDeleteConfirmOpen(false);
      }
    } catch (err: any) {
      if (currentUserIdRef.current === fetchForUserId) {
        console.error("Failed to delete summary:", err);
        setFeedback({ type: "error", message: "Failed to delete account summary" });
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDeletePositions = async () => {
    const fetchForUserId = userId;
    try {
      setDeletingPositions(true);
      clearFeedbackTimer();
      setFeedback(null);
      const result = await accountSummaryService.deleteAdminUserPositionsV2(fetchForUserId);
      if (currentUserIdRef.current === fetchForUserId) {
        setDeletePositionsConfirmOpen(false);
        // Show success message temporarily
        setFeedback({ type: "success", message: `Deleted ${result.deletedCount} position records successfully` });
        feedbackTimerRef.current = setTimeout(() => {
          if (currentUserIdRef.current === fetchForUserId) {
            setFeedback(null);
          }
          feedbackTimerRef.current = null;
        }, 3000);
      }
    } catch (err: any) {
      if (currentUserIdRef.current === fetchForUserId) {
        console.error("Failed to delete positions:", err);
        setFeedback({ type: "error", message: "Failed to delete positions" });
      }
    } finally {
      setDeletingPositions(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 p-4">
        <div className={appearance === "alpha" ? "h-4 bg-v7-mist rounded-v7-control w-1/4" : "h-4 bg-gray-200 rounded w-1/4"}></div>
        <div className={appearance === "alpha" ? "h-20 bg-v7-mist rounded-v7-control" : "h-20 bg-gray-200 rounded"}></div>
      </div>
    );
  }

  const handleDoubleClick = () => {
    if (summary) {
      setDetailModalOpen(true);
    }
  };

  return (
    <div 
      className={appearance === "alpha"? `rounded-v7-panel border border-v7-ink/15 bg-v7-white overflow-hidden ${summary ?"cursor-pointer" : ""}` : `rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"} overflow-hidden ${summary ? "cursor-pointer" : ""}`}
      onDoubleClick={handleDoubleClick}
      title={summary ? "Double-click to view full details" : undefined}
    >
      <div className={appearance === "alpha" ? "p-4 border-b border-v7-ink/15 flex items-center justify-between" : "p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"}>
        <h3 className={appearance === "alpha"? `font-semibold text-v7-ink` : `font-semibold ${isDarkMode ?"text-white" : "text-gray-900"}`}>
          IBKR Account Summary
        </h3>
        <div className="flex items-center space-x-2">
          {summary && (
            <>
              {!advisorMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/users/${userId}/dashboard`);
                  }}
                  className={appearance === "alpha" ? `p-1.5 rounded-v7-control transition-colors hover:bg-v7-paper text-v7-bronze-dark hover:text-v7-bronze-dark` : `p-1.5 rounded transition-colors ${
                    isDarkMode
                      ? "hover:bg-teal-900/30 text-teal-400 hover:text-teal-300"
                      : "hover:bg-teal-50 text-teal-500 hover:text-teal-700"
                  }`}
                  title="View User Dashboard"
                >
                  <LayoutDashboard className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDetailModalOpen(true);
                }}
                className={appearance === "alpha" ? `p-1.5 rounded-v7-control transition-colors hover:bg-v7-paper text-v7-bronze-dark hover:text-v7-bronze-dark` : `p-1.5 rounded transition-colors ${
                  isDarkMode
                    ? "hover:bg-blue-900/30 text-blue-400 hover:text-blue-300"
                    : "hover:bg-blue-50 text-blue-500 hover:text-blue-700"
                }`}
                title="View Full Details"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
              {!advisorMode && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletePositionsConfirmOpen(true);
                    }}
                    className={appearance === "alpha" ? `p-1.5 rounded-v7-control hover:bg-v7-paper text-v7-bronze-dark transition-colors` : `p-1.5 rounded hover:bg-orange-100 text-orange-500 transition-colors`}
                    title="Delete Positions"
                  >
                    <TrendingDown className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirmOpen(true);
                    }}
                    className={appearance === "alpha" ? `p-1.5 rounded-v7-control hover:bg-v7-rust/5 text-v7-rust transition-colors` : `p-1.5 rounded hover:bg-red-100 text-red-500 transition-colors`}
                    title="Delete Summary"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              fetchSummary(true);
            }}
            disabled={refreshing}
            className={appearance === "alpha" ? `p-1.5 rounded-v7-control transition-colors hover:bg-v7-paper text-v7-ink/60 hover:text-v7-ink` : `p-1.5 rounded transition-colors ${
              isDarkMode
                ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                : "hover:bg-gray-100 text-gray-500 hover:text-gray-900"
            }`}
            title="Refresh Data"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="p-4">
        {feedback && (
          <div className={appearance === "alpha"? `mb-4 p-3 rounded-v7-control text-sm ${feedback.type ==="success"
    ? "bg-v7-success/10 text-v7-success" : "bg-v7-rust/5 text-v7-rust"}` : `mb-4 p-3 rounded text-sm ${
            feedback.type === "success"
              ? isDarkMode ? "bg-green-900/30 text-green-300" : "bg-green-50 text-green-700"
              : isDarkMode ? "bg-red-900/30 text-red-300" : "bg-red-50 text-red-700"
          }`}>
            {feedback.message}
          </div>
        )}

        {!summary ? (
          <div className="text-center py-6">
            <p className={appearance === "alpha"? `text-sm text-v7-ink/60 mb-4` : `text-sm ${isDarkMode ?"text-gray-400" : "text-gray-500"} mb-4`}>
              No account summary available.
            </p>
            <button
              onClick={() => fetchSummary(true)}
              disabled={refreshing}
              className={appearance === "alpha" ? `px-4 py-2 rounded-v7-control text-sm font-medium transition-colors bg-v7-ink hover:bg-v7-ink text-v7-white` : `px-4 py-2 rounded text-sm font-medium transition-colors ${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {refreshing ? "Fetching..." : "Fetch from IBKR"}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={appearance === "alpha"? `text-xs text-v7-ink/60` : `text-xs ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>Account</p>
                <p className={appearance === "alpha"? `font-medium text-v7-ink` : `font-medium ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                  {summary.accountCode}
                </p>
              </div>
              <div>
                <p className={appearance === "alpha"? `text-xs text-v7-ink/60` : `text-xs ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>Type</p>
                <p className={appearance === "alpha"? `font-medium text-v7-ink` : `font-medium ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                  {summary.accountType}
                </p>
              </div>
              <div>
                <p className={appearance === "alpha"? `text-xs text-v7-ink/60` : `text-xs ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>Net Liquidation</p>
                <p className={appearance === "alpha"? `font-medium text-v7-ink` : `font-medium ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                  {formatCurrency(summary.netLiquidationSecurity)}
                </p>
              </div>
              <div>
                <p className={appearance === "alpha"? `text-xs text-v7-ink/60` : `text-xs ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>Buying Power</p>
                <p className={appearance === "alpha"? `font-medium text-v7-ink` : `font-medium ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                  {formatCurrency(summary.buyingPower)}
                </p>
              </div>
              <div>
                <p className={appearance === "alpha"? `text-xs text-v7-ink/60` : `text-xs ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>Available Funds</p>
                <p className={appearance === "alpha"? `font-medium text-v7-ink` : `font-medium ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                  {formatCurrency(summary.availableFundsSecurity)}
                </p>
              </div>
              <div>
                <p className={appearance === "alpha"? `text-xs text-v7-ink/60` : `text-xs ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>Excess Liquidity</p>
                <p className={appearance === "alpha"? `font-medium text-v7-ink` : `font-medium ${isDarkMode ?"text-white" : "text-gray-900"}`}>
                  {formatCurrency(summary.excessLiquiditySecurity)}
                </p>
              </div>
            </div>

            <div className={appearance === "alpha"? `text-xs text-v7-ink/60 pt-2 border-t border-v7-ink/15 flex items-center justify-between` : `text-xs ${isDarkMode ?"text-gray-500" : "text-gray-400"} pt-2 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex items-center justify-between`}>
              <span>Last updated: {new Date(summary.updatedDatetimeUtc).toLocaleString()}</span>
              <span className={appearance === "alpha"? `text-v7-bronze-dark` : `${isDarkMode ?"text-blue-400" : "text-blue-500"}`}>
                Double-click for full details
              </span>
            </div>

            {/* History Section (admin only) */}
            {!advisorMode && (
              <div className={appearance === "alpha"? `mt-4 pt-4 border-t border-v7-ink/15` : `mt-4 pt-4 border-t ${isDarkMode ?"border-gray-700" : "border-gray-200"}`}>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className={appearance === "alpha"? `flex items-center justify-between w-full text-sm font-medium text-v7-ink-soft hover:text-v7-ink` : `flex items-center justify-between w-full text-sm font-medium ${isDarkMode ?"text-gray-300 hover:text-white" : "text-gray-700 hover:text-gray-900"}`}
                >
                  <div className="flex items-center">
                    <History className="w-4 h-4 mr-2" />
                    History
                  </div>
                  {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showHistory && (
                  <div className="mt-4 space-y-2">
                    {loadingHistory ? (
                      <div className="text-center py-2">
                        <div className={appearance === "alpha" ? "animate-spin rounded-full h-4 w-4 border-b-2 border-v7-ink/25 mx-auto" : "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"}></div>
                      </div>
                    ) : history.length === 0 ? (
                      <p className={appearance === "alpha"? `text-sm text-center text-v7-ink/60` : `text-sm text-center ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                        No history available
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {history.map((record) => (
                          <div
                            key={record.id}
                            className={appearance === "alpha" ? `p-2 rounded-v7-control text-xs flex justify-between items-center bg-v7-paper` : `p-2 rounded text-xs flex justify-between items-center ${
                              isDarkMode ? "bg-gray-700/50" : "bg-gray-50"
                            }`}
                          >
                            <span className={appearance === "alpha" ? "text-v7-ink-soft" : isDarkMode ? "text-gray-300" : "text-gray-700"}>
                              {new Date(record.createdDatetimeUtc).toLocaleString()}
                            </span>
                            <span className={appearance === "alpha" ? "text-v7-ink/60" : isDarkMode ? "text-gray-500" : "text-gray-400"}>
                              ID: {record.id}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmationModal appearance={appearance}
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Account Summary"
        message="Are you sure you want to delete this account summary? This action cannot be undone."
        confirmText={deleting ? "Deleting..." : "Delete"}
        isDestructive={true}
      />

      <ConfirmationModal appearance={appearance}
        isOpen={deletePositionsConfirmOpen}
        onClose={() => setDeletePositionsConfirmOpen(false)}
        onConfirm={handleDeletePositions}
        title="Delete Account Positions"
        message="Are you sure you want to delete all position data for this user? This will remove all position snapshots and history. This action cannot be undone."
        confirmText={deletingPositions ? "Deleting..." : "Delete Positions"}
        isDestructive={true}
      />

      {summary && (
        <AdminAccountDetailModal appearance={appearance}
          isOpen={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          summary={summary}
          userName={userName}
        />
      )}
    </div>
  );
};

export default AdminAccountSummary;
